import { useState } from "react";
import { PatientEntityType } from "@/lib/supabase/supabaseTypes";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";
import { JobDocumentTypeEnum } from "@/dentlab/src/pages/job-page/job-document/job-document.types";
import { useExportActions } from "@/dentlab/src/hooks/actions/useExportActions";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import {
	dispatchPrint,
	isAutoPrintEnabled,
} from "@/dentlab/src/lib/utils/print-dispatch";
import { supabase } from "@/lib/supabase";
import { MessageType } from "@/lib/store/Realtime/message.types";
import { v4 as uuidv4 } from "uuid";

export function useJobPrintActions() {
	const { patientsLookup } = usePatientStore((state) => ({
		patientsLookup: state.patientsLookup,
	}));
	const { job } = useJobStore((state) => ({ job: state.job }));
	const { organization, clientsLookup } = useCentralStore((state) => ({
		organization: state.organization,
		clientsLookup: state.clientsLookup,
	}));
	const { practitionersLookup, guarantorLookup } = useCentralStore(
		(state) => ({
			practitionersLookup: state.practitionersLookup,
			guarantorLookup: state.guarantorLookup,
		})
	);
	const { exportJobOrderLabel, exportAuftragsblatt } = useExportActions();

	const [pdfViewer, setPdfViewer] = useState<{
		url: string;
		fileName: string;
	} | null>(null);
	const [loadingAuftragsblatt, setLoadingAuftragsblatt] = useState(false);
	const [loadingEtikett, setLoadingEtikett] = useState(false);

	const closePdfViewer = () => {
		if (pdfViewer) {
			URL.revokeObjectURL(pdfViewer.url);
			setPdfViewer(null);
		}
	};

	const handleCreateAuftragsblatt = async () => {
		setLoadingAuftragsblatt(true);
		try {
			await createAuftragsblatt();
		} finally {
			setLoadingAuftragsblatt(false);
		}
	};

	const createAuftragsblatt = async () => {
		const jobId = job?.id;
		if (!jobId || !organization || !job.client_id) {
			showNotification({
				message: "Fehlende Informationen für Auftragsblatt",
				type: "error",
			});
			return;
		}
		const client = clientsLookup[job.client_id];
		if (!client) {
			showNotification({
				message: "Auftraggeber nicht gefunden",
				type: "error",
			});
			return;
		}

		const patient = job.patient_id
			? patientsLookup[job.patient_id]
			: undefined;
		const practitioner = job.practitioner_id
			? practitionersLookup[job.practitioner_id]
			: undefined;
		const guarantor = job.guarantor_id
			? guarantorLookup[job.guarantor_id]
			: undefined;
		const qrCodeUrl = `${window.location.origin}/job/${jobId}`;

		let formSections: any[] | undefined;
		let formData: Record<string, any> | undefined;
		let formReferences: Record<string, string> | undefined;
		let formTitle: string | undefined;
		let odontogramData: any[] | undefined;
		let odontogramFields: any[] | undefined;
		const prefillMappedFieldIds = new Set<string>();

		try {
			const { data: channels } = await supabase
				.from(SupabaseTableEnum.CHANNELS)
				.select("id")
				.eq("job_id", jobId)
				.limit(1);

			if (channels && channels.length > 0) {
				const { data: messages } = await supabase
					.from("messages")
					.select("content")
					.eq("channel_id", channels[0].id)
					.order("created_at", { ascending: true })
					.limit(20);

				const requestMsg = messages?.find(
					(m: any) =>
						m.content?.type === MessageType.JOB_REQUEST_CUSTOM_V1
				);
				if (requestMsg?.content) {
					const content = requestMsg.content as any;
					formData = content.customFormData;
					formReferences = content.customFormReferences ?? {};
					formTitle = content.customFormTitle;

					if (
						content.customFormId &&
						content.customFormId !== "system-standard-form"
					) {
						const { data: formDef } = await supabase
							.from("forms")
							.select("definition, rules")
							.eq("id", content.customFormId)
							.single();
						if (formDef?.definition) {
							formSections = (formDef.definition as any).sections;
						}
						if (formDef?.rules && Array.isArray(formDef.rules)) {
							const dateTimeJobFields = new Set([
								"jobs.incoming_order_date",
								"jobs.incoming_order_time",
								"jobs.outgoing_order_date",
								"jobs.outgoing_order_time",
							]);
							for (const rule of formDef.rules as any[]) {
								if (
									rule.enabled &&
									rule.action?.type ===
										"prefill_job_fields" &&
									Array.isArray(rule.action.fieldMappings)
								) {
									for (const mapping of rule.action
										.fieldMappings) {
										if (
											dateTimeJobFields.has(
												mapping.jobFieldKey
											) &&
											mapping.valueSource ===
												"form_field" &&
											mapping.formFieldId
										) {
											prefillMappedFieldIds.add(
												mapping.formFieldId
											);
										}
									}
								}
							}
						}
					}

					if (!formSections && formData) {
						formSections = [
							{
								id: "fallback",
								title: "",
								fields: Object.entries(formData).map(
									([fieldId, val]: [string, any]) => ({
										id: fieldId,
										type: val.fieldType ?? "text",
										label:
											formReferences?.[fieldId] ??
											fieldId,
									})
								),
							},
						];
					}

					if (formSections && formData) {
						for (const section of formSections) {
							for (const field of section.fields) {
								if (
									field.type === "odontogram" &&
									formData[field.id]
								) {
									const toothVal =
										formData[field.id].fieldValue;
									if (Array.isArray(toothVal)) {
										odontogramData = toothVal;
										odontogramFields =
											field.odontogramFields;
									}
								}
							}
						}
					}
				}
			}
		} catch (err) {
			console.error("Failed to fetch job request data:", err);
		}

		let deliveryNoteItems: any[] | undefined;
		let deliveryNoteDocument: any | undefined;
		try {
			const { data: docs } = await supabase
				.from("job_documents")
				.select("*")
				.eq("job_id", jobId)
				.eq("type", JobDocumentTypeEnum.DeliveryNote)
				.order("created_at", { ascending: false })
				.limit(1);

			if (docs && docs.length > 0) {
				deliveryNoteDocument = docs[0];
				const { data: items } = await supabase
					.from("job_items")
					.select("*")
					.eq("job_document_id", docs[0].id);
				if (items && items.length > 0) {
					deliveryNoteItems = items;
				}
			}
		} catch (err) {
			console.error("Failed to fetch delivery note items:", err);
		}

		let jobFiles: Array<{
			name: string;
			url?: string;
			type: string;
		}> = [];
		try {
			const { data: files } = await supabase
				.from("files")
				.select("file_name, path_name, meta_data")
				.eq("job_id", jobId)
				.is("job_document_id", null)
				.is("deleted_at", null);

			if (files && files.length > 0) {
				const imageFiles = files.filter((f: any) =>
					/\.(jpg|jpeg|png|webp|gif)$/i.test(f.file_name ?? "")
				);
				const nonImageFiles = files.filter(
					(f: any) =>
						!/\.(jpg|jpeg|png|webp|gif)$/i.test(f.file_name ?? "")
				);

				if (imageFiles.length > 0) {
					const { data: urls } = await supabase.storage
						.from("v1")
						.createSignedUrls(
							imageFiles.map((f: any) => f.path_name),
							3600
						);
					imageFiles.forEach((f: any, i: number) => {
						jobFiles.push({
							name: f.file_name ?? "Datei",
							url: urls?.[i]?.signedUrl,
							type: (f.meta_data as any)?.type ?? "file",
						});
					});
				}
				nonImageFiles.forEach((f: any) => {
					jobFiles.push({
						name: f.file_name ?? "Datei",
						type: (f.meta_data as any)?.type ?? "file",
					});
				});
			}
		} catch (err) {
			console.error("Failed to fetch job files:", err);
		}

		const fileName = "Auftragsblatt_" + job.code + ".pdf";
		const filePath = organization.id + "/" + uuidv4() + "/" + fileName;

		const { success, error, blob } = await exportAuftragsblatt(
			{
				job,
				client,
				organization,
				patient: patient as PatientEntityType,
				practitioner,
				guarantor,
				qrCodeUrl,
				formSections,
				formData,
				formReferences,
				formTitle,
				odontogramData,
				odontogramFields,
				deliveryNoteItems,
				deliveryNoteDocument,
				excludedFieldIds:
					prefillMappedFieldIds.size > 0
						? Array.from(prefillMappedFieldIds)
						: undefined,
				jobFiles: jobFiles.length > 0 ? jobFiles : undefined,
			},
			filePath
		);

		if (!success || !blob) {
			showNotification({
				message: `Fehler beim Erstellen des Auftragsblatt: ${error}`,
				type: "error",
			});
			return;
		}

		const url = URL.createObjectURL(blob);
		setPdfViewer({ url, fileName });

		if (isAutoPrintEnabled("auftragsblatt")) {
			await dispatchPrint({
				documentType: "auftragsblatt",
				documentData: blob,
				fileName,
			});
		}
	};

	const handleCreateEtikett = async () => {
		setLoadingEtikett(true);
		try {
			await createEtikett();
		} finally {
			setLoadingEtikett(false);
		}
	};

	const createEtikett = async () => {
		const jobId = job?.id;
		if (!jobId || !organization || !job.client_id) {
			showNotification({
				message: `Fehler beim Erstellen der Auftragsetikett: Fehlende Informationen`,
				type: "error",
			});
			return;
		}

		const client = clientsLookup[job.client_id];
		if (!client) {
			showNotification({
				message: "Auftraggeber nicht gefunden",
				type: "error",
			});
			return;
		}

		let patientForLabel = undefined;
		if (job.patient_id) {
			patientForLabel = patientsLookup[job.patient_id];
		}

		const qrCodeUrl = `${window.location.origin}/job/${jobId}`;
		const fileName = "Auftragsetikett_" + job.code + ".pdf";
		const filePath = organization.id + "/" + uuidv4() + "/" + fileName;

		const { success, error, blob } = await exportJobOrderLabel(
			{
				job,
				client,
				organization,
				patient: patientForLabel as PatientEntityType,
				qrCodeUrl,
			},
			filePath
		);

		if (!success || !blob) {
			showNotification({
				message: `Fehler beim Erstellen der Auftragsetikett: ${error}`,
				type: "error",
			});
			return;
		}

		const url = URL.createObjectURL(blob);
		setPdfViewer({ url, fileName });

		if (isAutoPrintEnabled("etik_auftrag")) {
			await dispatchPrint({
				documentType: "etik_auftrag",
				documentData: blob,
				fileName,
			});
		}
	};

	return {
		pdfViewer,
		closePdfViewer,
		loadingAuftragsblatt,
		loadingEtikett,
		handleCreateAuftragsblatt,
		handleCreateEtikett,
	};
}
